(() => {
  'use strict';
  // Display transaction copy supplied by the API in the requested language.
  function localizeTransaction(record, strings, locale) {
    return {
      title: typeof record.txTypeLabel === 'string' ? record.txTypeLabel : ''
    };
  }
  function transactionMatches(record, tab, category = 'all') {
    if ((record.status === 'PENDING') !== (tab === 'pending')) return false;
    if (category === 'earned') return record.pointsDelta > 0;
    // Redemption is the documented user spending transaction. An expiry or
    // administrative deduction is not a coupon spend, even when its delta is negative.
    if (category === 'spent') return record.txType === 'REDEEM' && record.pointsDelta < 0;
    if (category === 'expired') return record.status === 'EXPIRED';
    return true;
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { localizeTransaction, transactionMatches };
  if (typeof document === 'undefined') return;
  async function start() {
    const contextNode = document.querySelector('[data-points-context]');
    const list = Array.from(document.querySelectorAll('.points-record-card__list, .rewards-page__records-list')).find(node => node.getBoundingClientRect().width > 0);
    if (!contextNode || !list || list.dataset.pointsReady || typeof window.createPointsPagination !== 'function') return;
    list.dataset.pointsReady = 'true';
    const track = action => window.trackPointsEvent?.(action);
    track('activity_view');
    const { root, region, strings } = JSON.parse(contextNode.textContent);
    const api = new window.PointsAPI(window.PointsAPI.selectedRegion(region));
    const locale = document.documentElement.lang || 'en';
    const card = list.closest('[data-points-activity]');
    const tabs = [...(card?.querySelectorAll('[data-activity-tab]') || [])];
    const filter = card?.querySelector('[data-activity-filter]');
    const selectorElement = filter?.querySelector('[data-component="selector"]');
    const filterToggle = selectorElement?.querySelector('.component-filter__state-layer');
    const filterDropdown = selectorElement?.querySelector('.component-filter__dropdown');
    const panelId = 'PointsActivity-' + Math.random().toString(36).slice(2);
    list.id = panelId; list.setAttribute('role', 'region');
    list.setAttribute('aria-label', strings.activity_title); list.tabIndex = 0;
    const status = document.createElement('p'); status.className = 'points-activity__status'; status.setAttribute('role', 'status');
    const more = document.createElement('button'); more.type = 'button'; more.className = 'points-activity__button'; more.hidden = true;
    list.after(status, more);
    let busy = false, retryAction, revision = 0, renderedCount = 0, selectedFilter = 'all';
    let activityRecords = [];
    let selector;
    let requestedPage = 1, loadedPage = 1, totalPages = 0, totalRecords = null;
    const pagination = window.createPointsPagination(list, strings, nextPage => { track('activity_page_change'); requestedPage = nextPage; loadPage(); }, { keepCurrentPage: true });
    function renderPage() {
      list.replaceChildren(); renderedCount = 0;
      render(activityRecords.slice(0, 5));
      pagination.update(requestedPage, totalPages, totalRecords);
    }
    function showError(error, retry) {
      track('activity_load_error');
      status.textContent = strings[error.code === 'login' ? 'login' : 'load_error'];
      more.hidden = false; more.textContent = strings[error.code === 'login' ? 'login' : 'retry'];
      retryAction = error.code === 'login' ? () => {
        sessionStorage.setItem('points-login-return', location.pathname + location.search);
        location.href = root + 'pages/login';
      } : retry;
    }
    function render(records) {
      for (const record of records) {
        const template = card?.querySelector('#list-item-template .component-list-item') || document.querySelector('#list-item-template .component-list-item');
        if (!template) throw new Error('Missing record template');
        const item = template.cloneNode(true);
        item.dataset.activityExpired = String(record.status === 'EXPIRED');
        const text = localizeTransaction(record, strings, locale);
        item.querySelector('.component-list-item__headline').textContent = text.title;
        const support = item.querySelector('.component-list-item__support-text');
        const date = window.PointsAPI.instant(record.createdAt);
        support.textContent = date ? new Date(date).toLocaleString(locale) : '';
        const delta = item.querySelector('.component-list-item__trailing-text');
        delta.textContent = (record.pointsDelta > 0 ? '+' : '') + record.pointsDelta.toLocaleString(locale);
        delta.dataset.points = record.pointsDelta >= 0 ? 'positive' : 'negative';
        list.append(item);
        renderedCount++;
      }
    }
    async function loadPage(reset = false) {
      if (busy) return;
      const version = revision;
      const category = selectedFilter;
      busy = true; more.disabled = true; more.hidden = true;
      status.textContent = reset ? strings.loading : '';
      pagination.setBusy(true, { delayedIndicator: !reset });
      list.setAttribute('aria-busy', 'true');
      if (reset) { list.replaceChildren(); renderedCount = 0; more.hidden = true; activityRecords = []; requestedPage = 1; loadedPage = 1; totalPages = 0; totalRecords = null; pagination.update(1, 0); }
      try {
        const data = await api.getTransactions(requestedPage, { status: category === 'all' ? '' : category });
        if (version !== revision) return;
        // The backend paginates the requested status. Filtering this page again
        // would make its rows disagree with the returned totals and page count.
        track('activity_load_success');
        activityRecords = data.records;
        totalPages = data.pages;
        totalRecords = data.total;
        loadedPage = requestedPage;
        renderPage();
        status.textContent = !renderedCount ? strings[totalPages > 1 ? 'activity_page_empty' : category !== 'all' ? 'activity_filtered_empty' : 'activity_empty'] : '';
        more.hidden = true;
      } catch (error) { if (version === revision) { pagination.update(loadedPage, totalPages, totalRecords); showError(error, () => loadPage(reset)); } }
      finally {
        if (version === revision) { busy = false; more.disabled = false; pagination.setBusy(false); list.setAttribute('aria-busy', 'false'); }
      }
    }
    function reloadActivity() {
      revision++; busy = false;
      loadPage(true);
    }
    function closeFilter() {
      filterToggle?.setAttribute('aria-expanded', 'false');
      filterDropdown?.setAttribute('hidden', '');
    }
    function selectStatus(value, label) {
      tabs.forEach(tab => tab.setAttribute('aria-pressed', String(tab.dataset.activityTab === value)));
      selector?.setSelected(value);
      filterToggle?.setAttribute('aria-label', strings.activity_filter + ': ' + label);
      list.setAttribute('aria-label', strings.activity_title + ': ' + label);
      closeFilter();
      if (selectedFilter === value) return;
      track('activity_filter_' + (['ACTIVE', 'PENDING', 'all'].includes(value) ? value.toLowerCase() : 'other'));
      selectedFilter = value;
      reloadActivity();
    }
    if (selectorElement && window.initSelector) {
      // component-grid renders desktop/mobile copies. Give the visible selector
      // a unique ID before using the same initializer as the policy page.
      selectorElement.id = panelId + '-Filter';
      filterToggle.disabled = true;
      selector = window.initSelector(selectorElement.id, [{ value: 'ACTIVE', label: strings.activity_posted }, { value: 'PENDING', label: strings.activity_pending }, { value: 'all', label: strings.activity_all }], {
        defaultLabel: strings.activity_all, showCount: false,
        onChange: result => {
          selectStatus(result.value, result.label);
          filterToggle.focus();
        }
      });
      selector.setSelected(selectedFilter);
      filterToggle.setAttribute('aria-label', strings.activity_filter + ': ' + strings.activity_all);
      filterDropdown.id = panelId + '-FilterOptions';
      filterToggle.setAttribute('aria-controls', filterDropdown.id);
      selectorElement.addEventListener('keydown', event => {
        if (event.key === 'Escape') { event.preventDefault(); closeFilter(); filterToggle.focus(); return; }
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (filterToggle.getAttribute('aria-expanded') !== 'true') filterToggle.click();
        const items = [...filterDropdown.querySelectorAll('button')];
        const index = items.indexOf(document.activeElement);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : index < 0 ? (event.key === 'ArrowUp' ? items.length - 1 : 0) : (index + (event.key === 'ArrowUp' ? items.length - 1 : 1)) % items.length;
        items[next]?.focus();
      });
    }
    tabs.forEach(tab => tab.addEventListener('click', () => selectStatus(tab.dataset.activityTab, tab.textContent.trim())));
    more.addEventListener('click', () => { track('activity_retry'); retryAction?.(); });
    list.replaceChildren();
    // Keep independent resources from blocking the list or overwriting its errors.
    function independentLoad(task, anchor) {
      const message = document.createElement('p');
      message.className = 'points-activity__status'; message.setAttribute('role', 'status');
      const retry = document.createElement('button');
      retry.type = 'button'; retry.className = 'points-activity__button'; retry.hidden = true;
      anchor.after(message, retry);
      async function run() {
        retry.hidden = true; retry.disabled = true; message.textContent = '';
        try { await task(); }
        catch (error) {
          const login = error.code === 'login';
          message.textContent = strings[login ? 'login' : 'load_error'];
          retry.textContent = strings[login ? 'login' : 'retry']; retry.hidden = false;
          retry.onclick = login ? () => {
            sessionStorage.setItem('points-login-return', location.pathname + location.search);
            location.href = root + 'pages/login';
          } : run;
        } finally { retry.disabled = false; }
      }
      return run();
    }
    independentLoad(async () => {
      const cachedBalance = await api.getCachedBalance();
      if (cachedBalance) {
        document.querySelectorAll('#points-number').forEach(el => { el.textContent = cachedBalance.available.toLocaleString(locale); });
        document.querySelectorAll('#expiry-notice, #expiry-notice-text').forEach(el => { el.textContent = strings.expiry_policy; });
      }
      const balance = await api.getBalance();
      document.querySelectorAll('#points-number').forEach(el => { el.textContent = balance.available.toLocaleString(locale); });
      document.querySelectorAll('#expiry-notice, #expiry-notice-text').forEach(el => { el.textContent = strings.expiry_policy; });
    }, [...document.querySelectorAll('#expiry-notice, #expiry-notice-text')].find(node => node.getBoundingClientRect().width > 0) || card);
    if (selector) independentLoad(async () => {
      const options = await api.getTransactionStatusOptions();
      tabs.forEach(tab => {
        const option = options.find(option => option.code === tab.dataset.activityTab);
        if (option) tab.textContent = option.label;
      });
      selector.setOptions([{ value: 'all', label: strings.activity_all }, ...options.map(option => ({ value: option.code, label: option.label }))]);
      selector.setSelected(selectedFilter);
      const label = selectedFilter === 'all' ? strings.activity_all : options.find(option => option.code === selectedFilter)?.label || tabs.find(tab => tab.dataset.activityTab === selectedFilter)?.textContent || strings.activity_posted;
      selectStatus(selectedFilter, label);
      filterToggle.disabled = false;
    }, card.querySelector('.points-activity__toolbar'));
    await loadPage(true);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  document.addEventListener('shopify:section:load', start);
  document.addEventListener('points:pagination-ready', start);
})();
